import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BookOpen, Plus, Loader2, Trash2, Edit2, Download, Eye, EyeOff, Link2, BarChart3 } from "lucide-react";

const ResourcesPage = () => {
  const { token } = useAuth();
  const [resources, setResources] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "general", file_url: "", is_published: false, require_email: false });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...headers, "Content-Type": "application/json" };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rRes, sRes] = await Promise.all([
        fetch(`${API}/staff/resources?limit=50`, { headers }),
        fetch(`${API}/staff/marketing-stats`, { headers }),
      ]);
      if (rRes.ok) { const d = await rRes.json(); setResources(d.resources); setTotal(d.total); }
      if (sRes.ok) setStats(await sRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const url = editing ? `${API}/staff/resources/${editing}` : `${API}/staff/resources`;
      const res = await fetch(url, { method: editing ? "PUT" : "POST", headers: jsonHeaders, body: JSON.stringify(form) });
      if (res.ok) { toast.success(editing ? "Resource updated" : "Resource created"); setShowForm(false); setEditing(null); resetForm(); fetchAll(); }
      else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Connection error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this resource?")) return;
    try {
      const res = await fetch(`${API}/staff/resources/${id}`, { method: "DELETE", headers });
      if (res.ok) { toast.success("Deleted"); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const resetForm = () => setForm({ title: "", description: "", category: "general", file_url: "", is_published: false, require_email: false });

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Free Resources</h1>
          <p className="mt-1 text-base text-stone-500">Upload and publish downloadable resources for lead capture.</p>
        </div>
        <Button onClick={() => { resetForm(); setEditing(null); setShowForm(true); }} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="create-resource-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Resource
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <MiniStat label="Total Resources" value={stats.resources?.total || 0} icon={BookOpen} />
          <MiniStat label="Published" value={stats.resources?.published || 0} icon={Eye} color="text-green-600" />
          <MiniStat label="Total Downloads" value={stats.resources?.total_downloads || 0} icon={Download} color="text-[#FF7A00]" />
        </div>
      )}

      {showForm && (
        <Card className="rounded-xl border-[#FF7A00]/30 p-5 space-y-4" data-testid="resource-form">
          <h3 className="font-bold text-stone-900">{editing ? "Edit resource" : "New resource"}</h3>
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Resource title" className="rounded-lg" data-testid="res-title" />
          <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={3} className="rounded-lg" data-testid="res-desc" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-stone-600 mb-1 block">Category</label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="nutrition_guide">Nutrition Guide</SelectItem>
                  <SelectItem value="meal_plan_sample">Meal Plan Sample</SelectItem>
                  <SelectItem value="health_tips">Health Tips</SelectItem>
                  <SelectItem value="recipe">Recipe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input value={form.file_url} onChange={e => setForm({ ...form, file_url: e.target.value })} placeholder="File URL (PDF, document link)" className="rounded-lg" data-testid="res-url" />
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} className="rounded border-stone-300 text-[#FF7A00]" />
              Published (visible to public)
            </label>
            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input type="checkbox" checked={form.require_email} onChange={e => setForm({ ...form, require_email: e.target.checked })} className="rounded border-stone-300 text-[#FF7A00]" />
              Require email to download (lead capture)
            </label>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="res-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
            </Button>
            <Button variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
          </div>
        </Card>
      )}

      {resources.length > 0 ? (
        <div className="space-y-2">
          {resources.map(r => (
            <Card key={r.resource_id} className="rounded-xl border-stone-200/60 p-4" data-testid={`res-${r.resource_id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${r.is_published ? "bg-green-100" : "bg-stone-100"}`}>
                  {r.is_published ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-stone-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-stone-900">{r.title}</span>
                    <Badge variant="outline" className="text-xs capitalize">{r.category?.replace(/_/g, " ")}</Badge>
                    {r.require_email && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Email required</Badge>}
                    {r.is_published && <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">Published</Badge>}
                  </div>
                  {r.description && <p className="text-xs text-stone-500 line-clamp-1">{r.description}</p>}
                  <p className="text-xs text-stone-400 mt-0.5">
                    <Download className="w-3 h-3 inline mr-1" />{r.download_count || 0} downloads
                    {r.file_url && <span className="ml-3"><Link2 className="w-3 h-3 inline mr-1" />{r.file_url.substring(0, 40)}...</span>}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setForm({ title: r.title, description: r.description || "", category: r.category || "general", file_url: r.file_url || "", is_published: r.is_published || false, require_email: r.require_email || false }); setEditing(r.resource_id); setShowForm(true); }}>
                    <Edit2 className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-xs text-red-500" onClick={() => handleDelete(r.resource_id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="rounded-xl border-stone-200/60 p-12 text-center">
          <BookOpen className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500">No resources yet. Add your first downloadable resource.</p>
        </Card>
      )}
    </div>
  );
};

const MiniStat = ({ label, value, icon: Icon, color = "text-stone-700" }) => (
  <Card className="rounded-xl border-stone-200/60 p-3 text-center">
    <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
    <p className={`text-lg font-bold ${color}`}>{value}</p>
    <p className="text-xs text-stone-400">{label}</p>
  </Card>
);

export default ResourcesPage;
